import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { convertAudioTime, getBlobDuration } from '../../utils/utils'
import { getStoryAudio } from '../../utils/storyAudio'
import './player.css'

const speedList = [
  { label: '0.6x', value: '0.6' },
  { label: '0.8x', value: '0.8' },
  { label: '1.0x', value: '1.0', normal: true },
  { label: '1.2x', value: '1.2' },
  { label: '1.4x', value: '1.4' },
]

interface IPlayerProps {
  audio: string
  handleClickWord?: any
  activeIndex?: string
}
const PLayer: React.FC<IPlayerProps> = ({ audio }: any) => {
  // Shared element: the Player is rendered twice (mobile + desktop) and the
  // karaoke highlighting reads playback position from the same element.
  const { t } = useTranslation('reader')
  const audioRef = useMemo(() => getStoryAudio(audio), [audio])
  const onMove = useRef<boolean>(false)
  const barRef = useRef(document.createElement('div'))
  const barAnchor = useRef(document.createElement('div'))
  const barProgress = useRef(document.createElement('div'))
  const popupRef = useRef<HTMLDivElement>(null)
  const [play, setPlay] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [playBackRate, setPlayBackRate] = useState({ label: '1.0x (Normal)', value: '1.0' })
  const [isDropOpen, setIsDropOpen] = useState(false)

  const onTranslateClose = () => {
    setIsDropOpen(false)
  }
  const onDocumentClick = (e) => {
    if (!popupRef?.current?.contains(e.target)) {
      setIsDropOpen(false)
    }
  }

  useEffect(() => {
    document.addEventListener('scroll', onTranslateClose)
    document.addEventListener('click', onDocumentClick)
    return () => {
      document.removeEventListener('scroll', onTranslateClose)
      document.removeEventListener('click', onDocumentClick)
    }
  }, [isDropOpen])

  useEffect(() => {
    return () => audioRef && audioRef.pause()
  }, [])

  useEffect(() => {
    const onLoadedMetadata = async () => {
      if (audioRef.duration !== Infinity) {
        setDuration(audioRef.duration)
      } else if (!duration) {
        try {
          const totalDuration = await getBlobDuration(audio)
          setDuration(totalDuration)
        } catch (err) {
          setDuration(audioRef.duration)
        }
      }
    }
    const onTimeUpdate = () => setCurrentTime(audioRef.currentTime)
    // Sync the play/pause icon from the element itself so both Player
    // instances (mobile + desktop) agree on the shared audio's state.
    const onPlayEvent = () => setPlay(true)
    const onPauseEvent = () => setPlay(false)

    audioRef.addEventListener('loadedmetadata', onLoadedMetadata)
    audioRef.addEventListener('timeupdate', onTimeUpdate)
    audioRef.addEventListener('play', onPlayEvent)
    audioRef.addEventListener('pause', onPauseEvent)
    // A remount against an already-loaded shared element gets no fresh
    // loadedmetadata event, so pull the current state directly.
    if (audioRef.readyState >= 1) onLoadedMetadata()
    setCurrentTime(audioRef.currentTime)
    setPlay(!audioRef.paused)

    return () => {
      audioRef.removeEventListener('loadedmetadata', onLoadedMetadata)
      audioRef.removeEventListener('timeupdate', onTimeUpdate)
      audioRef.removeEventListener('play', onPlayEvent)
      audioRef.removeEventListener('pause', onPauseEvent)
    }
  }, [audioRef])

  useEffect(() => {
    audioRef.playbackRate = parseFloat(playBackRate.value)
  }, [playBackRate])

  useEffect(() => {
    if (barProgress.current) {
      const clientRect = barProgress.current.getBoundingClientRect()
      const maxPosX = barProgress.current.clientWidth
      barProgress.current.addEventListener('mousemove', (event) => {
        onMove.current = true
        let posX = event.pageX - clientRect.left
        if (posX < 0) posX = 0
        if (posX > maxPosX) posX = maxPosX
        barAnchor.current.style.left = `${posX - 8}px`
        barAnchor.current.classList.add('anchor-moving')
      })
      barProgress.current.addEventListener('mouseleave', () => {
        onMove.current = false
        barAnchor.current.classList.remove('anchor-moving')
        //move barAnchor
        // const ratio = (currentTime ? currentTime * 100 / duration : 0.5)
        // const width =  ratio / 100 * (barProgress.current.clientWidth)
        // barAnchor.current.style.left = Math.floor(width).toString() + 'px'
      })
    }
    return () => {
      if (barProgress.current) {
        barProgress.current.removeEventListener('mousemove', () => {
          onMove.current = false
          barAnchor.current.classList.remove('anchor-moving')
        })
        barProgress.current.removeEventListener('mouseleave', () => {
          onMove.current = false
          barAnchor.current.classList.remove('anchor-moving')
        })
      }
    }
  }, [barProgress])

  // useEffect(() => {
  //   // add event scroll audio
  //   console.log("add event");
  //   document
  //     .getElementsByClassName(".bar-scroll")[0]
  //     ?.addEventListener("click", function (event) {
  //       alert("Hi!");
  //     });
  //   return () => {
  //     console.log("remove event");
  //     document
  //       .getElementsByClassName(".bar-scroll")[0]
  //       ?.removeEventListener("click", () => {});
  //   };
  // }, []);
  const onPlay = () => {
    if (!play) {
      audioRef.play()
    } else {
      audioRef.pause()
    }
    setPlay(!play)
  }

  // scroll video
  const onScrollAudio = (event) => {
    const clientRect = barProgress.current.getBoundingClientRect()
    const posX = event.pageX - clientRect.left - 8
    const ratio = posX / barProgress.current.clientWidth
    audioRef.currentTime = audioRef.duration * ratio
  }
  // use memo for time audio
  const durationTimeText = useMemo(() => convertAudioTime(duration), [duration])
  const ratio = currentTime ? (currentTime * 100) / duration : 0
  const width = (ratio / 100) * barProgress.current.clientWidth
  barRef.current.style.width = Math.ceil(ratio).toString() + '%'

  if (!onMove.current) {
    barAnchor.current.style.left = Math.floor(width).toString() + 'px'
  }

  const onChangeSpeedRate = (elm) => {
    setPlayBackRate(elm)
    setIsDropOpen(false)
  }

  return (
    <>
      {/* One flex row: play, track, speed. (These used to be webflow
          w-col-* float columns whose percentage widths were narrower than the
          fixed-size buttons inside them, so the controls spilled over the
          elapsed-time label on narrow screens.) */}
      <div className='section-audio player-row'>
        <div className='section-audio-play'>
          <div className='audio-play' onClick={onPlay}>
            <i className={!play ? 'fa fa-play' : 'fa fa-pause'}></i>
          </div>
        </div>
        <div className='section-progress-bar player-track'>
          <span>{convertAudioTime(currentTime)}</span>
          <div className='audio-progress-bar' ref={barProgress} style={{ position: 'relative' }}>
            <div className='bar' ref={barRef} />
            <div className='hide bar-scroll' onClick={onScrollAudio} />
            <div className='bar-anchor' ref={barAnchor} onClick={onScrollAudio} />
          </div>
          <span>{durationTimeText}</span>
        </div>
        <div className={isDropOpen ? 'overlay_background' : ''}></div>
        <div className='playback_box_speed_main_div' ref={popupRef}>
          <div className='speed_box' onClick={() => setIsDropOpen(!isDropOpen)}>
            <div className='speed_box_div'>
              <span className='speed_box_text'>{`${playBackRate.value}`}</span>
              {isDropOpen ? <i className='fa fa-chevron-up'></i> : <i className='fa fa-chevron-down'></i>}
            </div>
          </div>
          {isDropOpen && (
            <div className='playback_speed_box_div' style={{ position: 'absolute', bottom: 30, zIndex: 99 }}>
              <div className='playback_box_speed'>
                <span className='playback_box_speed_heading'>
                  {t('player.playbackSpeed')} <i onClick={() => setIsDropOpen(false)} className='fa fa-close' />
                </span>
                {speedList.map((elm, index) => (
                  <span key={index} onClick={() => onChangeSpeedRate(elm)}>
                    {elm.normal ? t('player.normalSpeed', { speed: elm.label }) : elm.label}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default PLayer
